/**
 * Security & Identity console.
 *
 * §95: the required tabs are Users, Roles & Permissions, Authentication, MFA,
 * Enterprise SSO, Directory Services, Integrations, Sessions, Audit Log,
 * Security Policy. None may be silently removed; a tab may be preserved while
 * the capability behind it is disabled (§5).
 *
 * §96 §97: these screens show what is ACTUALLY implemented. Every value renders
 * from server state — there is no hard-coded claim. §97's rule is "never display
 * a capability that cannot be verified", so an unknown value renders as unknown
 * rather than as a reassuring default.
 */

import { type FormEvent, useEffect, useState } from 'react';
import type { Permission, ScopeKind } from './adminTypes.js';
import type { AgentStatus, PlaybookEntry } from './CallCenter.js';
import { MASKED_CASE_OFFICER_NAME, wellnessFields } from '../field-wellness/validation.js';
import { MASKED_UAC_NAME, isProtectedUacName } from '../field-wellness/MaskedNameInput.js';
import { FieldAdministratorPanel } from '../field-wellness/FieldAdministratorPanel.js';
import { NoteTemplateManager } from '../field-wellness/NoteTemplateManager.js';
import {
  ArrowPathIcon, ArrowsRightLeftIcon, ChartBarSquareIcon, CheckIcon,
  DevicePhoneMobileIcon, KeyIcon, MapPinIcon, PhoneIcon, ShieldCheckIcon,
  TrashIcon, UserGroupIcon, UserPlusIcon,
} from '@heroicons/react/24/outline';

export const ADMIN_TABS = [
  { id: 'users', label: 'Users' },
  { id: 'roles', label: 'Roles & Permissions' },
  { id: 'authentication', label: 'Authentication' },
  { id: 'mfa', label: 'MFA' },
  { id: 'sso', label: 'Enterprise SSO' },
  { id: 'directory', label: 'Directory Services' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'call-routing', label: 'Call Roster' },
  { id: 'call-operations', label: 'Live Operations' },
  { id: 'field-wellness', label: 'Field Wellness' },
  { id: 'wellness-notes', label: 'Note Templates' },
  { id: 'field-administrator', label: 'Field Administrator' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'audit', label: 'Audit Log' },
  { id: 'policy', label: 'Security Policy' },
] as const;

export type AdminTab = (typeof ADMIN_TABS)[number]['id'];

export interface AdminUser {
  id: string;
  displayName: string;
  username: string | null;
  status: 'invited' | 'active' | 'disabled' | 'locked';
  roles: Array<{ role: string; scope: ScopeKind; scopeRef: string | null }>;
  lastLoginLabel: string | null;
  mfaEnrolled: boolean;
  loginLocked: boolean;
  lockedUntilLabel: string | null;
  communicationStatus?: 'not_requested' | 'pending' | 'approved' | 'denied' | 'disabled';
  admissionRequestId?: string | null;
  veritasId?: string | null;
}

export interface RoleDefinition {
  name: string;
  system: boolean;
  locked: boolean;
  permissions: Permission[];
}

export interface IntegrationStatus {
  name: string;
  /** §98: reflects real production state. */
  state: 'connected' | 'in_boundary' | 'policy' | 'disabled' | 'not_configured';
  detail: string;
}

export interface SecurityPolicyView {
  categories: Array<{ heading: string; rows: Array<[string, string]> }>;
}

export interface StarlinkAdminView {
  configured: boolean;
  state: 'connected' | 'attention' | 'not_configured';
  clientIdHint: string | null;
  lastSyncAt: string | null;
  lastErrorCode: string | null;
  terminalCount: number;
  teams: Array<{ id: string; name: string }>;
  terminals: Array<{
    id: string;
    label: string;
    identifierHint: string;
    teamId: string | null;
    teamName: string | null;
    reportedAt: string | null;
    status: 'online' | 'stale' | 'offline' | 'unassigned';
    signalPercent: number | null;
    alertCount: number;
  }>;
}

export interface CallRoutingData {
  enabled: boolean;
  outboundIdentity: string | null;
  recordingEnabled: boolean;
  playbook: PlaybookEntry[];
  queue: {
    id: string;
    name: string;
    extension: string;
    strategy: 'ring-all' | 'longest-idle-agent' | 'round-robin' | 'top-down';
  } | null;
  users: Array<{
    userId: string;
    displayName: string;
    email: string | null;
    userStatus: AdminUser['status'];
    selected: boolean;
    extension: string | null;
    agentStatus: AgentStatus;
    position: number | null;
    communicationStatus: 'not_requested' | 'pending' | 'approved' | 'denied' | 'disabled';
    admissionRequestId: string | null;
  }>;
}

export interface CallOperationsData {
  asOf: string;
  timezone: string;
  status: 'provisional' | 'final';
  freshness: { latestEventAt: string | null; lagSeconds: number | null; targetSeconds: number };
  presencePolicy: {
    inactivityThresholdSeconds: number;
    resumeGraceSeconds: number;
    restorationMode: 'prior_state' | 'prompt';
  };
  metrics: {
    offered: number;
    answered: number;
    handled: number;
    answerRate: number | null;
    averageSpeedSeconds: number;
  };
  queues: Array<{
    id: string; name: string; extension: string; serviceLevelSeconds: number;
    kind: 'service' | 'escalation'; waiting: number; active: number;
    answeredToday: number; unansweredToday: number;
  }>;
  agents: Array<{
    id: string; displayName: string; extension: string; status: AgentStatus;
    stateStartedAt: string; stateDurationSeconds: number; reason: string | null; queues: string;
    operationsRole: 'operator' | 'team_lead' | 'manager';
  }>;
  managerQueue: {
    id: string; name: string; extension: string; waiting: number; active: number;
  } | null;
  activeCalls: Array<{
    id: string; direction: 'inbound' | 'outbound' | 'internal'; state: string;
    remoteIdentity: string; startedAt: string; answeredAt: string | null;
    queue: { id: string; name: string; extension: string } | null;
    agent: { id: string; displayName: string; extension: string } | null;
    transferCount: number; controlState: 'pending' | 'dispatched' | null;
    controlDestinationExtension: string | null;
  }>;
  transferTargets: {
    queues: Array<{
      id: string; name: string; extension: string; kind: 'service' | 'escalation';
    }>;
    agents: Array<{
      id: string; displayName: string; extension: string; status: AgentStatus;
      operationsRole: 'team_lead' | 'manager';
    }>;
  };
  escalationTeam: Array<{
    agentId: string; displayName: string; extension: string; status: AgentStatus;
    operationsRole: 'operator' | 'team_lead' | 'manager';
  }>;
}

export interface AdminData {
  users: AdminUser[];
  roles: RoleDefinition[];
  allPermissions: Permission[];
  authentication: Array<[string, string]>;
  mfa: Array<[string, string]>;
  sso: Array<[string, string]>;
  directory: Array<[string, string]>;
  integrations: IntegrationStatus[];
  starlink: StarlinkAdminView;
  geography?: {
    dimensions: Array<{
      dimension: 'city' | 'county' | 'state' | 'zip' | 'area' | 'region' | 'cluster';
      values: Array<{ value: string; count: number }>;
    }>;
  };
  sessions: Array<{
    id: string; userLabel: string; createdLabel: string;
    lastSeenLabel: string; current: boolean;
  }>;
  audit: Array<{
    eventId: string; whenLabel: string; event: string;
    result: string; reasonCode: string | null;
    objectType: string | null; objectId: string | null;
    requestId: string | null; correlationId: string | null;
    sourceService: string | null; destinationService: string | null;
    details: Record<string, unknown>;
  }>;
  policy: SecurityPolicyView;
  callRouting: CallRoutingData;
  callOperations: CallOperationsData | null;
  fieldWellness: Array<{
    id: string; reference: string; formVersion: string;
    status: string; submittedAt: string; submittedBy: string;
    deviceLabel: string; answers: Record<string, string> | null;
  }>;
  fieldWellnessConfig: {
    activeOfficerId: string | null;
    officers: Array<{
      id: string; label: string; maskedName: string; active: boolean; createdAt: string;
    }>;
  };
  /** §43: step-up remaining, shown in the subtitle. */
  stepUpExpiresLabel: string | null;
}

export type DiagnosticTarget =
  | 'all' | 'application' | 'sip-edge' | 'pbx' | 'carrier' | 'transcription';

export interface DiagnosticCheck {
  target: Exclude<DiagnosticTarget, 'all'> | 'browser';
  label: string;
  ok: boolean;
  elapsedMs: number;
  observation: string;
  action: string;
}

export interface DiagnosticRun {
  traceId: string;
  ok: boolean;
  checks: DiagnosticCheck[];
}

export function AdminScreen({
  tab, data, accessMessage, accessReason, accessBusy, accessError, onVerifyAccess,
  onTabChange, onSelectUser, onCreateUser, onRevokeSession,
  onSaveCallRoster,
  onSaveCallPlaybook,
  onDecideCommunicationAccess,
  onTransferCall,
  onSaveEscalationTeam,
  onRunCallCenterDiagnostics,
  onRefreshAudit,
  onOpenCallCenter,
  onConnectStarlink,
  onDisconnectStarlink,
  onSyncStarlink,
  onCreateTeam,
  onSaveStarlinkAssignments,
  onAssignGeography,
  onAddFieldWellnessCaseOfficer,
  onSelectFieldWellnessCaseOfficer,
}: {
  tab: AdminTab;
  data: AdminData | null;
  accessMessage?: string | null;
  accessReason?: string | null;
  accessBusy?: boolean;
  accessError?: string | null;
  onVerifyAccess?: () => void;
  onTabChange: (t: AdminTab) => void;
  onSelectUser: (id: string) => void;
  onCreateUser: () => void;
  onRevokeSession: (id: string) => void;
  onSaveCallRoster: (queueId: string, userIds: string[]) => Promise<void>;
  onSaveCallPlaybook: (entries: PlaybookEntry[]) => Promise<void>;
  onDecideCommunicationAccess: (
    userId: string, decision: 'approve' | 'deny', reason?: string,
  ) => Promise<void>;
  onTransferCall: (id: string, body: {
    destinationKind: 'queue' | 'agent'; destinationId: string;
    reasonCode: string; reasonDetail?: string;
  }) => Promise<void>;
  onSaveEscalationTeam: (assignments: Array<{
    agentId: string; role: 'operator' | 'team_lead' | 'manager';
  }>) => Promise<void>;
  onRunCallCenterDiagnostics: (target: DiagnosticTarget) => Promise<DiagnosticRun>;
  onRefreshAudit: () => Promise<void>;
  onOpenCallCenter: () => void;
  onConnectStarlink: (body: { clientId: string; clientSecret: string }) => Promise<void>;
  onDisconnectStarlink: () => Promise<void>;
  onSyncStarlink: () => Promise<void>;
  onCreateTeam: (name: string) => Promise<{ id: string; name: string }>;
  onSaveStarlinkAssignments: (
    assignments: Array<{ terminalId: string; teamId: string | null }>,
  ) => Promise<void>;
  onAssignGeography?: (body: {
    dimension: string; value: string; teamId: string | null;
  }) => Promise<{ updated: number }>;
  onAddFieldWellnessCaseOfficer: (body: {
    firstName: string; lastName: string;
  }) => Promise<void>;
  onSelectFieldWellnessCaseOfficer: (officerId: string) => Promise<void>;
}) {
  return (
    <div className="pt-4 min-w-0">
      <h1 className="mb-1">Security &amp; Identity</h1>
      <p className="text-base text-neutral-700 mb-4">
        Administration
        {data?.stepUpExpiresLabel &&
          ` · step-up verified with security key · expires in ${data.stepUpExpiresLabel}`}
      </p>

      <div role="tablist" aria-label="Administration" className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-none desktop:flex-wrap">
        {ADMIN_TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => onTabChange(t.id)}
            className={`seg shrink-0 ${tab === t.id ? 'seg-active' : ''}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {!data && !accessMessage && <p className="text-base text-neutral-700">Loading…</p>}
      {accessMessage && (
        <section className="card border-accent-300 bg-accent-100" role="status">
          <div className="flex items-center gap-2 mb-2">
            <KeyIcon className="w-6 h-6 text-accent-700 shrink-0" aria-hidden />
            <h6 className="text-accent-800">Security verification required</h6>
          </div>
          <p className="text-base">{accessMessage}</p>
          {(accessReason === 'WEBAUTHN_REQUIRED' || accessReason === 'STEP_UP_REQUIRED') &&
            onVerifyAccess && (
              <>
                <button
                  type="button"
                  className="btn btn-primary mt-3"
                  disabled={accessBusy}
                  onClick={onVerifyAccess}
                >
                  <KeyIcon className="w-5 h-5" aria-hidden />
                  {accessBusy ? 'Verifying…' : 'Verify with security key'}
                </button>
                <p className="mt-2 text-sm text-neutral-700">
                  Use your registered passkey, Touch ID, or hardware security key.
                  Access remains elevated for five minutes.
                </p>
              </>
            )}
          {accessError && <p role="alert" className="form-error mt-3">{accessError}</p>}
        </section>
      )}

      {data && tab === 'users' && <UsersTab users={data.users} onSelect={onSelectUser}
        onCreate={onCreateUser} onDecideCommunicationAccess={onDecideCommunicationAccess} />}
      {data && tab === 'roles' && (
        <RolesTab roles={data.roles} permissions={data.allPermissions} />
      )}
      {data && tab === 'authentication' && (
        <DefinitionList heading="Password" rows={data.authentication} />
      )}
      {data && tab === 'mfa' && <DefinitionList heading="Factors" rows={data.mfa} />}
      {data && tab === 'sso' && <DefinitionList heading="Enterprise SSO" rows={data.sso} />}
      {data && tab === 'directory' && (
        <DefinitionList heading="Directory Services" rows={data.directory} />
      )}
      {data && tab === 'integrations' && (
        <IntegrationsTab items={data.integrations} starlink={data.starlink}
          teams={data.starlink.teams} geography={data.geography ?? { dimensions: [] }}
          onConnect={onConnectStarlink} onDisconnect={onDisconnectStarlink}
          onSync={onSyncStarlink} onCreateTeam={onCreateTeam}
          onSaveAssignments={onSaveStarlinkAssignments}
          onAssignGeography={onAssignGeography} />
      )}
      {data && tab === 'call-routing' && (
        <CallRoutingTab data={data.callRouting} onSave={onSaveCallRoster}
          onSavePlaybook={onSaveCallPlaybook}
          onDecideCommunicationAccess={onDecideCommunicationAccess} />
      )}
      {data && tab === 'call-operations' && data.callOperations && (
        <CallOperationsTab data={data.callOperations} onTransfer={onTransferCall}
          onSaveTeam={onSaveEscalationTeam} />
      )}
      {data && tab === 'field-wellness' && <FieldWellnessAdmin records={data.fieldWellness}
        config={data.fieldWellnessConfig} onAddOfficer={onAddFieldWellnessCaseOfficer}
        onSelectOfficer={onSelectFieldWellnessCaseOfficer} />}
      {data && tab === 'field-administrator' && <FieldAdministratorPanel />}
      {data && tab === 'wellness-notes' && <NoteTemplateManager />}
      {data && tab === 'sessions' && (
        <SessionsTab sessions={data.sessions} onRevoke={onRevokeSession} />
      )}
      {data && tab === 'audit' && <AuditTab entries={data.audit}
        onRunDiagnostics={onRunCallCenterDiagnostics}
        onRefresh={onRefreshAudit} onOpenCallCenter={onOpenCallCenter} />}
      {data && tab === 'policy' && <PolicyTab policy={data.policy} />}
    </div>
  );
}

export function CallRoutingTab({
  data, onSave, onSavePlaybook, onDecideCommunicationAccess,
}: {
  data: CallRoutingData;
  onSave: (queueId: string, userIds: string[]) => Promise<void>;
  onSavePlaybook: (entries: PlaybookEntry[]) => Promise<void>;
  onDecideCommunicationAccess: (
    userId: string, decision: 'approve' | 'deny', reason?: string,
  ) => Promise<void>;
}) {
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(data.users.filter((user) => user.selected).map((user) => user.userId)),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [playbook, setPlaybook] = useState<PlaybookEntry[]>(data.playbook);
  const [playbookSaving, setPlaybookSaving] = useState(false);
  const [playbookError, setPlaybookError] = useState<string | null>(null);
  const [accessBusy, setAccessBusy] = useState<string | null>(null);
  const [accessError, setAccessError] = useState<string | null>(null);

  useEffect(() => {
    setSelected(new Set(data.users.filter((user) => user.selected).map((user) => user.userId)));
    setPlaybook(data.playbook);
  }, [data]);

  if (!data.queue) {
    return <section className="card"><h2 className="text-h4">Call roster</h2>
      <p className="text-base text-neutral-700 mt-2">Create the call queue before assigning operators.</p>
    </section>;
  }

  const eligible = data.users.filter((user) => user.userStatus === 'active' || user.userStatus === 'invited');
  const selectedCount = selected.size;
  const save = async () => {
    if (selectedCount < 1) {
      setError('Select at least one person to receive calls.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(data.queue!.id, eligible.filter((user) => selected.has(user.userId))
        .map((user) => user.userId));
    } catch {
      setError('The roster could not be saved. Your previous selection is still active.');
    } finally { setSaving(false); }
  };

  const updateEntry = (index: number, field: keyof PlaybookEntry, value: string | string[]) => {
    setPlaybook((current) => current.map((entry, entryIndex) =>
      entryIndex === index ? { ...entry, [field]: value } : entry));
  };
  const savePlaybook = async () => {
    setPlaybookSaving(true);
    setPlaybookError(null);
    try { await onSavePlaybook(playbook); }
    catch { setPlaybookError('The call guide could not be saved. The prior version is still active.'); }
    finally { setPlaybookSaving(false); }
  };

  return <section className="desktop:max-w-[1000px]">
    <div className="card mb-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-h6 uppercase tracking-[.08em] text-accent-700">Incoming calls</p>
          <h2 className="text-h4">{data.queue.name}</h2>
          <p className="text-sm text-neutral-700 mt-1">Queue extension {data.queue.extension}</p>
        </div>
        <span className="tag tag-neutral">Round robin</span>
      </div>
      <p className="text-base text-neutral-700 mt-3">
        Each new call goes to the next selected person who is Available. Anyone on Break,
        Offline, disabled, or removed from this list is skipped automatically.
      </p>
    </div>

    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
      <p className="text-base text-neutral-700">{selectedCount} selected</p>
      <div className="flex gap-2">
        <button type="button" className="btn btn-secondary" disabled={saving}
          onClick={() => setSelected(new Set(eligible.map((user) => user.userId)))}>Select all</button>
        <button type="button" className="btn btn-secondary" disabled={saving}
          onClick={() => setSelected(new Set())}>Clear</button>
      </div>
    </div>

    <ul className="grid gap-2">
      {data.users.map((user) => {
        const available = user.userStatus === 'active' || user.userStatus === 'invited';
        const checked = selected.has(user.userId);
        return <li key={user.userId} className={`card ${!available ? 'opacity-60' : ''}`}>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex min-w-0 flex-1 items-center gap-3 cursor-pointer">
              <input type="checkbox" className="h-5 w-5 accent-accent" checked={checked}
                disabled={!available || saving}
                onChange={(event) => setSelected((current) => {
                  const next = new Set(current);
                  if (event.target.checked) next.add(user.userId); else next.delete(user.userId);
                  return next;
                })} />
              <UserGroupIcon className="h-6 w-6 text-accent-700 shrink-0" aria-hidden />
              <span className="flex-1 min-w-0">
                <span className="block font-heading font-semibold text-base">{user.displayName}</span>
                <span className="block text-sm text-neutral-700 truncate">
                  {user.email ?? 'Workspace SSO only'} · {user.extension ? `extension ${user.extension}` : 'extension assigned when saved'}
                </span>
                <span className="block text-xs text-neutral-700 mt-1">
                  Managed phone: {communicationStatusLabel(user.communicationStatus)}
                </span>
              </span>
              <span className="tag tag-neutral shrink-0">{user.userStatus}</span>
            </label>
            {user.communicationStatus === 'pending' && user.admissionRequestId && <div
              className="flex gap-2 pl-8 min-[620px]:pl-0">
              <button type="button" className="btn btn-primary px-3"
                disabled={accessBusy !== null}
                onClick={async () => {
                  setAccessBusy(user.userId); setAccessError(null);
                  try { await onDecideCommunicationAccess(user.userId, 'approve'); }
                  catch { setAccessError('The access decision could not be saved.'); }
                  finally { setAccessBusy(null); }
                }}>
                <ShieldCheckIcon className="h-5 w-5" aria-hidden />
                {accessBusy === user.userId ? 'Saving…' : 'Approve'}
              </button>
              <button type="button" className="btn btn-secondary px-3"
                disabled={accessBusy !== null}
                onClick={async () => {
                  setAccessBusy(user.userId); setAccessError(null);
                  try { await onDecideCommunicationAccess(user.userId, 'deny'); }
                  catch { setAccessError('The access decision could not be saved.'); }
                  finally { setAccessBusy(null); }
                }}>
                Deny
              </button>
            </div>}
          </div>
        </li>;
      })}
    </ul>
    {accessError && <p className="form-error mt-3" role="alert">{accessError}</p>}
    {error && <p className="form-error mt-3" role="alert">{error}</p>}
    <button type="button" className="btn btn-primary mt-4" disabled={saving || selectedCount < 1}
      onClick={() => void save()}>{saving ? 'Saving…' : 'Save call roster'}</button>

    <div className="mt-8 pt-6 border-t border-neutral-300">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-h6 uppercase tracking-[.08em] text-accent-700">Operator guidance</p>
          <h2 className="text-h3">Conversation playbook</h2>
          <p className="text-sm text-neutral-700 mt-1">
            Maintain the approved scripts and questions shown by the live response coach.
          </p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={() => setPlaybook((current) => [...current, {
          id: `draft-${Date.now()}`, category: 'General', title: 'New guide entry', triggers: [],
          response: 'Enter the approved response.', followUp: 'Enter the next question.',
        }])}>Add guide entry</button>
      </div>
      <div className="grid gap-3">
        {playbook.map((entry, index) => <article key={entry.id} className="card">
          <div className="grid desktop:grid-cols-[180px_1fr_auto] gap-3 items-end">
            <label className="field"><span>Category</span><input className="input" maxLength={80}
              value={entry.category} onChange={(event) => updateEntry(index, 'category', event.target.value)} /></label>
            <label className="field"><span>Title</span><input className="input" maxLength={160}
              value={entry.title} onChange={(event) => updateEntry(index, 'title', event.target.value)} /></label>
            <button type="button" className="btn btn-ghost text-alert-700" disabled={playbook.length <= 1}
              onClick={() => setPlaybook((current) => current.filter((_, entryIndex) => entryIndex !== index))}>
              Remove
            </button>
          </div>
          <label className="field mt-3"><span>Match when the caller says, comma separated</span>
            <input className="input" value={entry.triggers.join(', ')}
              onChange={(event) => updateEntry(index, 'triggers', event.target.value.split(',')
                .map((term) => term.trim()).filter(Boolean))} /></label>
          <label className="field mt-3"><span>Suggested response</span>
            <textarea className="input min-h-[76px]" maxLength={2000} value={entry.response}
              onChange={(event) => updateEntry(index, 'response', event.target.value)} /></label>
          <label className="field mt-3"><span>Next question</span>
            <textarea className="input min-h-[64px]" maxLength={1000} value={entry.followUp}
              onChange={(event) => updateEntry(index, 'followUp', event.target.value)} /></label>
        </article>)}
      </div>
      {playbookError && <p className="form-error mt-3" role="alert">{playbookError}</p>}
      <button type="button" className="btn btn-primary mt-4" disabled={playbookSaving || playbook.length < 1}
        onClick={() => void savePlaybook()}>{playbookSaving ? 'Saving…' : 'Save conversation playbook'}</button>
    </div>
  </section>;
}

function communicationStatusLabel(status: CallRoutingData['users'][number]['communicationStatus']) {
  return {
    not_requested: 'not requested',
    pending: 'approval requested',
    approved: 'approved',
    denied: 'denied',
    disabled: 'disabled',
  }[status];
}

export function CallOperationsTab({ data, onTransfer, onSaveTeam }: {
  data: CallOperationsData;
  onTransfer: (id: string, body: {
    destinationKind: 'queue' | 'agent'; destinationId: string;
    reasonCode: string; reasonDetail?: string;
  }) => Promise<void>;
  onSaveTeam: (assignments: Array<{
    agentId: string; role: 'operator' | 'team_lead' | 'manager';
  }>) => Promise<void>;
}) {
  const stale = data.freshness.lagSeconds !== null
    && data.freshness.lagSeconds > data.freshness.targetSeconds;
  const [selectedCallId, setSelectedCallId] = useState('');
  const [destination, setDestination] = useState('');
  const [reasonCode, setReasonCode] = useState('supervisor_support');
  const [reasonDetail, setReasonDetail] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [teamRoles, setTeamRoles] = useState<Record<string, 'operator' | 'team_lead' | 'manager'>>(
    () => Object.fromEntries(data.escalationTeam.map((member) => [member.agentId, member.operationsRole])),
  );
  const [teamDirty, setTeamDirty] = useState(false);
  const [teamBusy, setTeamBusy] = useState(false);
  const [teamError, setTeamError] = useState<string | null>(null);
  useEffect(() => {
    if (!teamDirty) {
      setTeamRoles(Object.fromEntries(
        data.escalationTeam.map((member) => [member.agentId, member.operationsRole]),
      ));
    }
  }, [data.escalationTeam, teamDirty]);

  const selectedCall = data.activeCalls.find((call) => call.id === selectedCallId) ?? null;
  const [destinationKind, destinationId] = destination.split(':') as [
    'queue' | 'agent' | '', string | undefined,
  ];
  const destinationLabel = destinationKind === 'queue'
    ? data.transferTargets.queues.find((target) => target.id === destinationId)?.name
    : destinationKind === 'agent'
      ? data.transferTargets.agents.find((target) => target.id === destinationId)?.displayName
      : null;

  async function transferNow() {
    if (!selectedCall || !destinationId || !destinationKind) return;
    setTransferBusy(true); setTransferError(null);
    try {
      await onTransfer(selectedCall.id, {
        destinationKind, destinationId, reasonCode,
        reasonDetail: reasonDetail.trim() || undefined,
      });
      setReviewing(false); setDestination(''); setReasonDetail('');
    } catch (cause) {
      setTransferError(cause instanceof Error ? cause.message : 'The transfer could not be requested.');
    } finally { setTransferBusy(false); }
  }

  async function saveTeam() {
    const assignments = data.escalationTeam.map((member) => ({
      agentId: member.agentId, role: teamRoles[member.agentId] ?? 'operator' as const,
    }));
    if (!assignments.some((item) => item.role === 'team_lead' || item.role === 'manager')) {
      setTeamError('Choose at least one manager or team lead.');
      return;
    }
    setTeamBusy(true); setTeamError(null);
    try {
      await onSaveTeam(assignments);
      setTeamDirty(false);
    } catch (cause) {
      setTeamError(cause instanceof Error ? cause.message : 'The escalation team could not be saved.');
    } finally { setTeamBusy(false); }
  }

  const metricCards = [
    { label: 'Calls offered', value: String(data.metrics.offered), note: 'Eligible external offers today' },
    { label: 'Calls answered', value: String(data.metrics.answered), note: 'Connected to an operator today' },
    { label: 'Calls handled', value: String(data.metrics.handled), note: 'Terminal disposition recorded' },
    { label: 'Answer rate', value: data.metrics.answerRate === null
      ? 'Not enough data' : `${Math.round(data.metrics.answerRate * 100)}%`,
      note: 'Answered divided by eligible offered' },
  ];
  return <div className="space-y-5">
    <section aria-labelledby="live-operations-title">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-h6 uppercase tracking-[.08em] text-accent-700">Protected management</p>
          <h2 id="live-operations-title" className="text-h3">Live operations</h2>
          <p className="text-sm text-neutral-700 mt-1">
            Watch demand, assign escalation coverage, and transfer active calls. Audio listening,
            whisper, and barge controls are disabled.
          </p>
        </div>
        <div className="text-right">
          <span className="tag tag-neutral capitalize">{data.status}</span>
          <p className="text-xs text-neutral-700 mt-1">Timezone: {data.timezone.replace('_', ' ')}</p>
        </div>
      </div>
      {stale && <p className="form-error mt-3" role="status">
        Live events are {data.freshness.lagSeconds} seconds behind. Treat current state as incomplete.
      </p>}
    </section>

    <section className="border border-accent-400 bg-accent-100 p-4"
      aria-labelledby="manager-queue-title">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <UserGroupIcon className="h-6 w-6 text-accent-700 shrink-0" aria-hidden />
          <div>
            <h3 id="manager-queue-title" className="text-h4">Manager escalation queue</h3>
            <p className="text-sm text-neutral-700 mt-1">
              Operators use one Escalate button. Available team leads and managers receive the call.
            </p>
          </div>
        </div>
        {data.managerQueue ? <span className="tag tag-neutral">
          Extension {data.managerQueue.extension}
        </span> : <span className="tag tag-alert">Not configured</span>}
      </div>
      {data.managerQueue && <dl className="grid grid-cols-2 max-w-sm gap-3 mt-4">
        <div><dt className="text-sm text-neutral-700">Waiting</dt>
          <dd className="font-heading text-h3 font-semibold tabular-nums">{data.managerQueue.waiting}</dd></div>
        <div><dt className="text-sm text-neutral-700">Active</dt>
          <dd className="font-heading text-h3 font-semibold tabular-nums">{data.managerQueue.active}</dd></div>
      </dl>}
    </section>

    <section aria-labelledby="active-calls-title">
      <div className="flex items-center gap-2">
        <PhoneIcon className="h-5 w-5 text-accent-700" aria-hidden />
        <h3 id="active-calls-title" className="text-h4">Active calls</h3>
        <span className="tag tag-neutral">{data.activeCalls.length}</span>
      </div>
      {data.activeCalls.length === 0
        ? <p className="border border-neutral-300 bg-neutral-100 p-3 mt-2 text-sm text-neutral-700">
            No call is active right now.
          </p>
        : <div className="grid gap-2 mt-2">
          {data.activeCalls.map((call) => <article key={call.id}
            className={`border p-3 ${selectedCallId === call.id
              ? 'border-accent-600 bg-accent-100' : 'border-neutral-300 bg-neutral-100'}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold tabular-nums">{call.remoteIdentity}</p>
                <p className="text-sm text-neutral-700">
                  {call.agent ? `${call.agent.displayName}, extension ${call.agent.extension}` : 'Waiting for an operator'}
                </p>
                <p className="text-xs text-neutral-700 mt-1">
                  {call.queue ? call.queue.name : 'Direct call'} · {call.direction} · {call.state.replaceAll('_', ' ')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {call.controlState && <span className="tag tag-neutral capitalize">
                  Transfer {call.controlState}
                </span>}
                <button type="button" className="btn btn-secondary"
                  disabled={Boolean(call.controlState)}
                  onClick={() => {
                    setSelectedCallId(call.id); setReviewing(false); setTransferError(null);
                  }}>
                  {selectedCallId === call.id ? 'Selected' : 'Manage'}
                </button>
              </div>
            </div>
          </article>)}
        </div>}

      {selectedCall && <div className="border border-accent-400 bg-bg p-4 mt-3">
        <div className="flex items-center gap-2">
          <ArrowsRightLeftIcon className="h-5 w-5 text-accent-700" aria-hidden />
          <h4 className="font-heading font-semibold">Transfer selected call</h4>
        </div>
        {!reviewing ? <>
          <label className="field mt-3" htmlFor="manager-transfer-target"><span>Send to</span></label>
          <select id="manager-transfer-target" className="input" value={destination}
            onChange={(event) => setDestination(event.target.value)}>
            <option value="">Choose a queue or person</option>
            <optgroup label="Queues">
              {data.transferTargets.queues.map((target) => <option key={target.id}
                value={`queue:${target.id}`}>{target.name}, extension {target.extension}</option>)}
            </optgroup>
            <optgroup label="Team leads and managers">
              {data.transferTargets.agents.map((target) => <option key={target.id}
                value={`agent:${target.id}`} disabled={target.status !== 'available'}>
                {target.displayName}, extension {target.extension}, {target.status.replaceAll('_', ' ')}
              </option>)}
            </optgroup>
          </select>
          <label className="field mt-3" htmlFor="manager-transfer-reason"><span>Reason</span></label>
          <select id="manager-transfer-reason" className="input" value={reasonCode}
            onChange={(event) => setReasonCode(event.target.value)}>
            <option value="supervisor_support">Supervisor support</option>
            <option value="customer_request">Caller request</option>
            <option value="complex_issue">Complex issue</option>
            <option value="complaint">Complaint</option>
            <option value="safety">Safety concern</option>
            <option value="other">Other</option>
          </select>
          <label className="field mt-3" htmlFor="manager-transfer-detail"><span>Handoff note, optional</span></label>
          <textarea id="manager-transfer-detail" className="input min-h-[80px]" maxLength={500}
            value={reasonDetail} onChange={(event) => setReasonDetail(event.target.value)} />
          <button type="button" className="btn btn-primary mt-3" disabled={!destination}
            onClick={() => setReviewing(true)}>Review transfer</button>
        </> : <div className="border border-neutral-300 bg-neutral-100 p-3 mt-3">
          <p className="font-semibold">Confirm transfer</p>
          <p className="text-sm text-neutral-700 mt-1">
            Move {selectedCall.remoteIdentity} to {destinationLabel ?? 'the selected destination'}.
            The current operator will leave the call when PBX control succeeds.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <button type="button" className="btn btn-secondary" disabled={transferBusy}
              onClick={() => setReviewing(false)}>Back</button>
            <button type="button" className="btn btn-primary" disabled={transferBusy}
              onClick={() => void transferNow()}>{transferBusy ? 'Transferring…' : 'Transfer now'}</button>
          </div>
        </div>}
        {transferError && <p className="form-error mt-3" role="alert">{transferError}</p>}
      </div>}
    </section>

    <section aria-labelledby="operations-metrics-title">
      <div className="flex items-center gap-2">
        <ChartBarSquareIcon className="h-5 w-5 text-accent-700" aria-hidden />
        <h3 id="operations-metrics-title" className="text-h4">Today</h3>
      </div>
      <div className="grid grid-cols-1 min-[560px]:grid-cols-2 desktop:grid-cols-4 gap-2 mt-2">
        {metricCards.map((metric) => <article key={metric.label}
          className="border border-neutral-300 bg-neutral-100 p-3">
          <p className="text-sm text-neutral-700">{metric.label}</p>
          <p className="font-heading text-h3 font-semibold tabular-nums mt-1">{metric.value}</p>
          <p className="text-xs text-neutral-700 mt-1">{metric.note}</p>
        </article>)}
      </div>
      <details className="mt-2 border border-neutral-300 bg-bg p-3 text-sm">
        <summary className="cursor-pointer font-semibold text-accent-700">Measurement rules</summary>
        <dl className="grid min-[720px]:grid-cols-3 gap-3 mt-3">
          <div><dt className="text-neutral-700">Window</dt><dd>Current local day</dd></div>
          <div><dt className="text-neutral-700">Status</dt><dd>Provisional until event reconciliation</dd></div>
          <div><dt className="text-neutral-700">Refresh target</dt><dd>{data.freshness.targetSeconds} seconds</dd></div>
        </dl>
      </details>
    </section>

    <section aria-labelledby="queue-health-title">
      <h3 id="queue-health-title" className="text-h4">Queue health</h3>
      {data.queues.length === 0 ? <p className="text-sm text-neutral-700 mt-2">No active queue is configured.</p>
        : <div className="grid grid-cols-1 desktop:grid-cols-2 gap-2 mt-2">
          {data.queues.map((queue) => <article key={queue.id}
            className="border border-neutral-300 bg-neutral-100 p-3">
            <div className="flex items-start justify-between gap-3">
              <div><h4 className="font-semibold">{queue.name}</h4>
                <p className="text-sm text-neutral-700">Extension {queue.extension}</p></div>
              <span className="tag tag-neutral">Goal {queue.serviceLevelSeconds}s</span>
            </div>
            <dl className="grid grid-cols-4 gap-2 mt-3 text-sm">
              <div><dt className="text-neutral-700">Waiting</dt><dd className="font-semibold tabular-nums">{queue.waiting}</dd></div>
              <div><dt className="text-neutral-700">Active</dt><dd className="font-semibold tabular-nums">{queue.active}</dd></div>
              <div><dt className="text-neutral-700">Answered</dt><dd className="font-semibold tabular-nums">{queue.answeredToday}</dd></div>
              <div><dt className="text-neutral-700">Unanswered</dt><dd className="font-semibold tabular-nums">{queue.unansweredToday}</dd></div>
            </dl>
          </article>)}
        </div>}
    </section>

    <section aria-labelledby="operator-state-title">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <UserGroupIcon className="h-5 w-5 text-accent-700" aria-hidden />
          <h3 id="operator-state-title" className="text-h4">Operator state</h3>
        </div>
        <span className="text-sm text-neutral-700">
          Auto Break at {Math.round(data.presencePolicy.inactivityThresholdSeconds / 60)} minutes
        </span>
      </div>
      <div className="overflow-x-auto border border-neutral-300 mt-2">
        <table className="w-full min-w-[680px] text-sm">
          <thead className="bg-neutral-100"><tr>
            <th className="text-left p-3">Operator</th><th className="text-left p-3">Extension</th>
            <th className="text-left p-3">State</th><th className="text-left p-3">Duration</th>
            <th className="text-left p-3">Queues</th><th className="text-left p-3">Reason</th>
          </tr></thead>
          <tbody className="divide-y divide-neutral-300">
            {data.agents.map((agent) => <tr key={agent.id}>
              <td className="p-3 font-semibold">{agent.displayName}</td>
              <td className="p-3 tabular-nums">{agent.extension}</td>
              <td className="p-3 capitalize">{agent.status.replaceAll('_', ' ')}</td>
              <td className="p-3 tabular-nums">{formatOperationsDuration(agent.stateDurationSeconds)}</td>
              <td className="p-3">{agent.queues || 'No queue'}</td>
              <td className="p-3 text-neutral-700">{agent.reason?.replaceAll('_', ' ') ?? 'Not reported'}</td>
            </tr>)}
          </tbody>
        </table>
      </div>
    </section>

    <section aria-labelledby="escalation-team-title">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 id="escalation-team-title" className="text-h4">Escalation coverage</h3>
          <p className="text-sm text-neutral-700 mt-1">
            Managers ring first, then team leads. Operators remain on the regular roster.
          </p>
        </div>
        <button type="button" className="btn btn-primary" disabled={!teamDirty || teamBusy}
          onClick={() => void saveTeam()}>{teamBusy ? 'Saving…' : 'Save coverage'}</button>
      </div>
      <div className="overflow-x-auto border border-neutral-300 mt-2">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="bg-neutral-100"><tr>
            <th className="text-left p-3">Person</th><th className="text-left p-3">Extension</th>
            <th className="text-left p-3">Current state</th><th className="text-left p-3">Escalation role</th>
          </tr></thead>
          <tbody className="divide-y divide-neutral-300">
            {data.escalationTeam.map((member) => <tr key={member.agentId}>
              <td className="p-3 font-semibold">{member.displayName}</td>
              <td className="p-3 tabular-nums">{member.extension}</td>
              <td className="p-3 capitalize">{member.status.replaceAll('_', ' ')}</td>
              <td className="p-3"><select className="input min-w-[160px]"
                aria-label={`Escalation role for ${member.displayName}`}
                value={teamRoles[member.agentId] ?? 'operator'}
                onChange={(event) => {
                  setTeamRoles((current) => ({ ...current, [member.agentId]: event.target.value as
                    'operator' | 'team_lead' | 'manager' }));
                  setTeamDirty(true); setTeamError(null);
                }}>
                <option value="operator">Operator</option>
                <option value="team_lead">Team lead</option>
                <option value="manager">Manager</option>
              </select></td>
            </tr>)}
          </tbody>
        </table>
      </div>
      {teamError && <p className="form-error mt-3" role="alert">{teamError}</p>}
    </section>
  </div>;
}

function formatOperationsDuration(totalSeconds: number) {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function UsersTab({
  users, onSelect, onCreate, onDecideCommunicationAccess,
}: {
  users: AdminUser[];
  onSelect: (id: string) => void;
  onCreate: () => void;
  onDecideCommunicationAccess: (
    userId: string, decision: 'approve' | 'deny', reason?: string,
  ) => Promise<void>;
}) {
  const [accessBusy, setAccessBusy] = useState<string | null>(null);
  const [accessError, setAccessError] = useState<string | null>(null);

  const decide = async (userId: string, decision: 'approve' | 'deny') => {
    setAccessBusy(`${userId}:${decision}`);
    setAccessError(null);
    try {
      await onDecideCommunicationAccess(userId, decision);
    } catch {
      setAccessError('The communications decision could not be saved. No access was changed.');
    } finally {
      setAccessBusy(null);
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-base text-neutral-700">{users.length} users · registration is administrator controlled</p>
        <button className="btn btn-primary shrink-0" onClick={onCreate}>
          <UserPlusIcon className="w-5 h-5" aria-hidden />Create user
        </button>
      </div>
      <ul className="grid gap-2">
      {users.map((u) => {
        const initials = u.displayName.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase();
        return (
        <li key={u.id} className="card p-0 overflow-hidden">
          <button type="button"
            onClick={() => onSelect(u.id)}
            className="w-full p-3 flex items-center gap-3 text-left min-h-touch
                       hover:bg-neutral-200 transition-colors"
          >
            <span className="w-10 h-10 rounded-full bg-accent-100 text-accent-800 flex items-center justify-center font-heading font-semibold shrink-0">{initials}</span>
            <span className="flex-1 min-w-0">
              <span className="block font-heading font-semibold text-h4">{u.displayName}</span>
              <span className="block text-base text-neutral-700 truncate">
                {u.roles.map((r) => r.role).join(', ') || 'No role'}
                {u.roles[0] && ` · ${scopeLabel(u.roles[0].scope)}`}
              </span>
              <span className="block text-sm text-neutral-600 truncate">{u.username ?? 'Workspace SSO only'}{u.lastLoginLabel && ` · last seen ${u.lastLoginLabel}`}</span>
              <span className="block text-sm text-neutral-700 mt-1">
                Managed communications: {(u.communicationStatus ?? 'not_requested').replaceAll('_', ' ')}
                {u.veritasId && ` · ${u.veritasId}`}
              </span>
            </span>

            {/* §43: an administrator without WebAuthn is an exception. */}
            {!u.mfaEnrolled && <span className="tag tag-alert shrink-0 mt-1">No MFA</span>}
            <StatusTag status={effectiveStatus(u)} />
          </button>
          {u.communicationStatus === 'pending' && u.admissionRequestId &&
            <div className="border-t border-neutral-300 bg-neutral-100 p-3 flex flex-wrap items-center gap-2">
              <span className="text-sm text-neutral-700 flex-1 min-w-[220px]">
                This person requested access to the managed VERITAS communications service.
              </span>
              <button type="button" className="btn btn-secondary"
                disabled={accessBusy !== null} onClick={() => void decide(u.id, 'deny')}>
                {accessBusy === `${u.id}:deny` ? 'Denying…' : 'Deny'}
              </button>
              <button type="button" className="btn btn-primary"
                disabled={accessBusy !== null} onClick={() => void decide(u.id, 'approve')}>
                {accessBusy === `${u.id}:approve` ? 'Approving…' : 'Approve'}
              </button>
            </div>}
        </li>
      );})}
      </ul>
      {accessError && <p className="form-error mt-3" role="alert">{accessError}</p>}
    </section>
  );
}

function scopeLabel(scope: ScopeKind): string {
  return { own: 'Own records', team: 'Assigned team', region: 'Assigned region', org: 'Organization-wide' }[scope];
}

function StatusTag({ status }: { status: AdminUser['status'] }) {
  const alert = status === 'disabled' || status === 'locked';
  return (
    <span className={`tag shrink-0 mt-1 ${alert ? 'tag-alert' : 'tag-neutral'}`}>
      {status}
    </span>
  );
}

function effectiveStatus(user: AdminUser): AdminUser['status'] {
  if (user.status === 'disabled') return 'disabled';
  if (user.status === 'locked' || user.loginLocked) return 'locked';
  return user.status;
}

/**
 * §34: the baseline roles plus dedicated AV review authority.
 * Super Administrator is locked and its checkboxes are not editable (§36).
 */
function RolesTab({
  roles, permissions,
}: { roles: RoleDefinition[]; permissions: Permission[] }) {
  return (
    <section>
      <p className="text-base text-neutral-700 mb-3">Six built-in roles. AV review access can be added to any user without replacing their main role.</p>
    <div className="overflow-x-auto card p-0">
      <table className="w-full text-base border-collapse">
        <caption className="sr-only">Role permission matrix</caption>
        <thead>
          <tr>
            <th scope="col" className="text-left py-2 pr-4 font-heading text-h6 uppercase">
              Permission
            </th>
            {roles.map((r) => (
              <th
                key={r.name}
                scope="col"
                className="text-center py-3 px-3 font-heading text-h6 uppercase whitespace-nowrap"
              >
                {r.name}
                {r.locked && <span className="block normal-case text-neutral-600">locked</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {permissions.map((p) => (
            <tr key={p} className="border-t border-neutral-300">
              <th scope="row" className="text-left py-3 px-3 font-mono text-sm font-normal whitespace-nowrap">{p}</th>
              {roles.map((r) => (
                <td key={r.name} className="py-3 px-3 text-center">
                  <span aria-label={`${r.name} ${p}: ${r.permissions.includes(p) ? 'allowed' : 'not allowed'}`}
                    className={`inline-flex w-6 h-6 rounded-full items-center justify-center border ${r.permissions.includes(p) ? 'bg-accent text-white border-accent' : 'border-neutral-300 text-transparent'}`}>
                    <CheckIcon className="w-4 h-4" aria-hidden />
                  </span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </section>
  );
}

function DefinitionList({ heading, rows }: { heading: string; rows: Array<[string, string]> }) {
  return (
    <section>
      <h6 className="text-accent-700 mb-2">{heading}</h6>
      <dl className="grid gap-2 desktop:grid-cols-2">
        {rows.map(([k, v]) => (
          <div key={k} className="card flex items-center gap-3">
            {heading === 'Factors' ? <KeyIcon className="w-6 h-6 text-accent-700 shrink-0" aria-hidden /> : <ShieldCheckIcon className="w-6 h-6 text-accent-700 shrink-0" aria-hidden />}
            <div className="flex-1"><dt className="font-heading font-semibold text-base">{k}</dt><dd className="text-sm text-neutral-700">{v}</dd></div>
            {(v === 'enabled' || v === 'yes') && <span className="tag bg-accent-100 text-accent-800">Active</span>}
          </div>
        ))}
      </dl>
    </section>
  );
}

/** §98: shows what is actually connected. A deferred provider reads disabled. */
function IntegrationsTab({
  items, starlink, teams, onConnect, onDisconnect, onSync, onCreateTeam,
  onSaveAssignments, geography, onAssignGeography,
}: {
  items: IntegrationStatus[];
  starlink: StarlinkAdminView;
  teams: Array<{ id: string; name: string }>;
  geography: NonNullable<AdminData['geography']>;
  onConnect: (body: { clientId: string; clientSecret: string }) => Promise<void>;
  onDisconnect: () => Promise<void>;
  onSync: () => Promise<void>;
  onCreateTeam: (name: string) => Promise<{ id: string; name: string }>;
  onSaveAssignments: (
    assignments: Array<{ terminalId: string; teamId: string | null }>,
  ) => Promise<void>;
  onAssignGeography?: (body: {
    dimension: string; value: string; teamId: string | null;
  }) => Promise<{ updated: number }>;
}) {
  const label: Record<IntegrationStatus['state'], string> = {
    connected: 'Connected',
    in_boundary: 'In boundary',
    policy: 'Policy',
    disabled: 'Disabled',
    not_configured: 'Not configured',
  };
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [newTeamName, setNewTeamName] = useState('');
  const [teamNotice, setTeamNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<
    'connect' | 'sync' | 'save' | 'disconnect' | 'create-team' | 'geography' | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [geoDimension, setGeoDimension] = useState('cluster');
  const [geoValue, setGeoValue] = useState('');
  const [geoTeam, setGeoTeam] = useState('');
  const [geoNotice, setGeoNotice] = useState<string | null>(null);
  const geoValues = geography.dimensions.find((item) => item.dimension === geoDimension)?.values ?? [];

  useEffect(() => {
    setAssignments(Object.fromEntries(
      starlink.terminals.map((terminal) => [terminal.id, terminal.teamId ?? '']),
    ));
  }, [starlink.terminals]);

  const run = async (
    action: NonNullable<typeof busy>,
    operation: () => Promise<void>,
  ) => {
    setBusy(action);
    setError(null);
    try {
      await operation();
    } catch (cause) {
      const reason = typeof cause === 'object' && cause && 'reason' in cause
        ? String((cause as { reason?: unknown }).reason ?? '') : '';
      const message: Record<string, string> = {
        AUTH_FAILED: 'Starlink rejected the client ID or secret.',
        PERMISSION_DENIED: 'The service account needs Device telemetry: View and Device management: View.',
        RATE_LIMITED: 'Starlink is temporarily limiting requests. Wait a minute and refresh.',
        TIMEOUT: 'Starlink did not answer in time. Check the connection and try again.',
        STEP_UP_REQUIRED: 'Verify with your security key, then save again.',
        WEBAUTHN_REQUIRED: 'A registered security key is required for this change.',
        TEAM_EXISTS: 'A team with that name already exists.',
        VALIDATION_FAILED: 'Enter a team name between 1 and 80 characters.',
      };
      setError(message[reason] ?? 'The change could not be saved. Try again.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="desktop:max-w-[980px] space-y-4">
      <section className="card border-accent-300" aria-labelledby="geography-heading">
        <div className="flex items-start gap-3">
          <UserGroupIcon className="w-7 h-7 text-accent-700 shrink-0" aria-hidden />
          <div>
            <h3 id="geography-heading" className="text-xl">Geographic team assignment</h3>
            <p className="text-sm text-neutral-700 mt-1">
              Imported records stay unassigned until you apply a team to a geographic group.
              Reapply the same group with Not assigned to clear its team.
            </p>
          </div>
        </div>

        <form className="mt-5 grid gap-4 desktop:grid-cols-3" onSubmit={(event) => {
          event.preventDefault();
          if (!geoValue || !onAssignGeography) return;
          setGeoNotice(null);
          void run('geography', async () => {
            const result = await onAssignGeography({
              dimension: geoDimension, value: geoValue, teamId: geoTeam || null,
            });
            setGeoNotice(`${result.updated} record${result.updated === 1 ? '' : 's'} updated.`);
          });
        }}>
          <label className="field" htmlFor="geo-dimension"><span>Group records by</span>
            <select id="geo-dimension" className="input" value={geoDimension}
              onChange={(event) => { setGeoDimension(event.target.value); setGeoValue(''); }}>
              {geography.dimensions.map((item) => (
                <option key={item.dimension} value={item.dimension}>
                  {item.dimension === 'zip' ? 'ZIP code' : item.dimension[0].toUpperCase() + item.dimension.slice(1)}
                </option>
              ))}
            </select>
          </label>
          <label className="field" htmlFor="geo-value"><span>Geographic value</span>
            <select id="geo-value" className="input" value={geoValue} required
              onChange={(event) => setGeoValue(event.target.value)}>
              <option value="">Select a value</option>
              {geoValues.map((item) => (
                <option key={item.value} value={item.value}>{item.value} ({item.count})</option>
              ))}
            </select>
          </label>
          <label className="field" htmlFor="geo-team"><span>Team</span>
            <select id="geo-team" className="input" value={geoTeam}
              onChange={(event) => setGeoTeam(event.target.value)}>
              <option value="">Not assigned</option>
              {teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
            </select>
          </label>
          <div className="desktop:col-span-3 flex flex-wrap items-center gap-3">
            <button className="btn btn-primary" type="submit"
              disabled={busy !== null || !geoValue || !onAssignGeography}>
              {busy === 'geography' ? 'Applying...' : 'Apply assignment'}
            </button>
            {geoNotice && <p role="status" className="text-sm text-accent-800">{geoNotice}</p>}
          </div>
        </form>

        <form className="mt-5 border-t border-neutral-300 pt-4" onSubmit={(event) => {
          event.preventDefault();
          const name = newTeamName.trim();
          if (!name) return;
          setTeamNotice(null);
          void run('create-team', async () => {
            const team = await onCreateTeam(name);
            setNewTeamName('');
            setTeamNotice(`${team.name} is ready. Choose it from a terminal menu below.`);
          });
        }}>
          <div className="desktop:flex desktop:items-end desktop:gap-3">
            <label className="field flex-1" htmlFor="geography-new-team"><span>Create a team</span>
              <input id="geography-new-team" className="input" value={newTeamName}
                maxLength={80} autoComplete="off" placeholder="Example: Central Team"
                onChange={(event) => setNewTeamName(event.target.value)} />
            </label>
            <button className="btn btn-secondary mt-3 desktop:mt-0" type="submit"
              disabled={busy !== null || !newTeamName.trim()}>
              {busy === 'create-team' ? 'Creating...' : 'Create team'}
            </button>
          </div>
          {teamNotice && <p role="status" className="text-sm text-accent-800 mt-2">{teamNotice}</p>}
        </form>
        {error && <p role="alert" className="form-error mt-4">{error}</p>}
      </section>

      <section className="card border-accent-300" aria-labelledby="starlink-heading">
        <div className="flex flex-wrap items-start gap-3">
          <MapPinIcon className="w-7 h-7 text-accent-700 shrink-0" aria-hidden />
          <div className="flex-1 min-w-[220px]">
            <h3 id="starlink-heading" className="text-xl">Starlink team locations</h3>
            <p className="text-sm text-neutral-700 mt-1">
              Shows each assigned terminal as an approximate Starlink coverage area on the team map.
              This is not exact employee GPS.
            </p>
          </div>
          <span className={`tag ${starlink.state === 'attention' ? 'tag-alert' : 'tag-neutral'}`}>
            {starlink.state === 'connected' ? 'Connected'
              : starlink.state === 'attention' ? 'Needs attention' : 'Not connected'}
          </span>
        </div>

        {!starlink.configured ? (
          <form className="mt-5 grid gap-4 desktop:grid-cols-2" onSubmit={(event) => {
            event.preventDefault();
            void run('connect', async () => {
              await onConnect({ clientId: clientId.trim(), clientSecret });
              setClientSecret('');
            });
          }}>
            <div>
              <label className="field" htmlFor="starlink-client-id"><span>Client ID</span></label>
              <input id="starlink-client-id" className="input" value={clientId}
                autoCapitalize="none" autoCorrect="off" spellCheck={false} maxLength={300}
                required onChange={(event) => setClientId(event.target.value)} />
            </div>
            <div>
              <label className="field" htmlFor="starlink-client-secret"><span>Client secret</span></label>
              <input id="starlink-client-secret" className="input" type="password"
                value={clientSecret} autoComplete="off" maxLength={2000} required
                onChange={(event) => setClientSecret(event.target.value)} />
            </div>
            <div className="desktop:col-span-2 flex flex-wrap items-center gap-3">
              <button className="btn btn-primary" type="submit" disabled={busy !== null}>
                {busy === 'connect' ? 'Connecting...' : 'Connect Starlink'}
              </button>
              <a className="btn btn-ghost px-0" href="https://www.starlink.com/account/settings"
                target="_blank" rel="noreferrer noopener">Open Starlink settings</a>
            </div>
            <p className="desktop:col-span-2 text-sm text-neutral-700">
              Create a V2 API service account with Device telemetry: View and Device management: View.
              Enter the credentials here, never in chat or email. The secret is encrypted and cannot be read back.
            </p>
          </form>
        ) : (
          <div className="mt-5">
            <div className="flex flex-wrap items-center gap-3 border-y border-neutral-300 py-3">
              <div className="flex-1 min-w-[220px] text-sm text-neutral-700">
                <span className="block font-semibold text-neutral-900">
                  {starlink.terminalCount} terminal{starlink.terminalCount === 1 ? '' : 's'} found
                </span>
                <span>
                  Account {starlink.clientIdHint}
                  {starlink.lastSyncAt
                    ? ` · updated ${new Date(starlink.lastSyncAt).toLocaleString()}`
                    : ' · waiting for first update'}
                </span>
                {starlink.lastErrorCode && (
                  <span className="block text-alert-800 mt-1">Last refresh: {starlink.lastErrorCode}</span>
                )}
              </div>
              <button className="btn btn-secondary" type="button" disabled={busy !== null}
                onClick={() => void run('sync', onSync)}>
                <ArrowPathIcon className={`w-5 h-5 ${busy === 'sync' ? 'animate-spin' : ''}`} aria-hidden />
                Refresh
              </button>
              <button className="btn btn-ghost text-alert-800" type="button" disabled={busy !== null}
                onClick={() => {
                  if (!window.confirm('Disconnect Starlink and remove its cached terminal locations?')) return;
                  void run('disconnect', onDisconnect);
                }}>
                <TrashIcon className="w-5 h-5" aria-hidden />
                Disconnect
              </button>
            </div>

            <h4 className="text-lg mt-5">Assign terminals to teams</h4>
            <p className="text-sm text-neutral-700 mb-3">
              Use the nickname from your Starlink account, then choose the team carrying that terminal.
            </p>
            {starlink.terminals.length === 0 ? (
              <p className="border border-neutral-300 rounded-lg p-4 text-neutral-700">No terminals were returned. Check service-account access, then refresh.</p>
            ) : (
              <ul className="divide-y divide-neutral-300 border-y border-neutral-300">
                {starlink.terminals.map((terminal) => (
                  <li key={terminal.id} className="py-3 grid gap-3 desktop:grid-cols-[minmax(220px,1fr)_minmax(220px,320px)] desktop:items-center">
                    <div className="min-w-0">
                      <span className="block font-semibold truncate">{terminal.label}</span>
                      <span className="block text-sm text-neutral-700">
                        {terminal.identifierHint} · {terminal.status}
                        {terminal.signalPercent === null ? '' : ` · signal ${terminal.signalPercent}%`}
                        {terminal.alertCount ? ` · ${terminal.alertCount} alert${terminal.alertCount === 1 ? '' : 's'}` : ''}
                      </span>
                    </div>
                    <label className="sr-only" htmlFor={`starlink-team-${terminal.id}`}>
                      Team for {terminal.label}
                    </label>
                    <select id={`starlink-team-${terminal.id}`} className="input"
                      value={assignments[terminal.id] ?? ''}
                      onChange={(event) => setAssignments((current) => ({
                        ...current, [terminal.id]: event.target.value,
                      }))}>
                      <option value="">Not assigned</option>
                      {teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
                    </select>
                  </li>
                ))}
              </ul>
            )}
            {starlink.terminals.length > 0 && (
              <button className="btn btn-primary mt-4" type="button" disabled={busy !== null}
                onClick={() => void run('save', () => onSaveAssignments(
                  starlink.terminals.map((terminal) => ({
                    terminalId: terminal.id,
                    teamId: assignments[terminal.id] || null,
                  })),
                ))}>
                {busy === 'save' ? 'Saving...' : 'Save team assignments'}
              </button>
            )}
          </div>
        )}
        {error && <p role="alert" className="form-error mt-4">{error}</p>}
      </section>

      <ul className="grid gap-3 desktop:grid-cols-2">
        {items.filter((item) => item.name !== 'Starlink team locations').map((i) => (
          <li key={i.name} className="card flex items-start gap-3 min-h-[120px]">
            <ShieldCheckIcon className="w-6 h-6 text-accent-700 shrink-0" aria-hidden />
            <span className="flex-1">
              <span className="block font-heading font-semibold text-base">{i.name}</span>
              <span className="block text-sm text-neutral-700">{i.detail}</span>
            </span>
            <span className={`tag shrink-0 ${i.state === 'disabled' ? 'tag-alert' : 'tag-neutral'}`}>
              {label[i.state]}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FieldWellnessAdmin({ records, config, onAddOfficer, onSelectOfficer }: {
  records: AdminData['fieldWellness'];
  config: AdminData['fieldWellnessConfig'];
  onAddOfficer: (body: { firstName: string; lastName: string }) => Promise<void>;
  onSelectOfficer: (officerId: string) => Promise<void>;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = config.officers.find((officer) => officer.id === config.activeOfficerId);

  async function addOfficer(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onAddOfficer({ firstName, lastName });
      setFirstName('');
      setLastName('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The case officer could not be saved.');
    } finally { setBusy(false); }
  }

  async function selectOfficer(officerId: string) {
    setBusy(true);
    setError(null);
    try {
      await onSelectOfficer(officerId);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The case officer could not be selected.');
    } finally { setBusy(false); }
  }

  return <section aria-labelledby="field-wellness-admin-title">
    <div>
      <h2 id="field-wellness-admin-title" className="text-h3">Field wellness records</h2>
      <p className="mt-1 text-sm text-neutral-700">
        Managed device submissions, decrypted only for this verified administrator session.
      </p>
    </div>
    <section className="card mt-5" aria-labelledby="case-officer-config-title">
      <p className="text-h6 uppercase tracking-[.08em] text-accent-700">Protected configuration</p>
      <h3 id="case-officer-config-title" className="text-h4 mt-1">Case officer assignment</h3>
      <p className="mt-2 text-sm text-neutral-700">
        One selection applies to every Field Wellness form. Names are encrypted at entry,
        masked on every record screen, and inserted only inside the protected submission.
      </p>
      <div className={`mt-4 border p-3 ${active
        ? 'border-presence-active bg-neutral-100' : 'border-alert-300 bg-alert-100'}`} role="status">
        <span className="block text-xs uppercase tracking-[.08em] text-neutral-700">Active assignment</span>
        <strong className="mt-1 block">{active
          ? `${active.label} · ${MASKED_CASE_OFFICER_NAME}` : 'No case officer selected'}</strong>
      </div>

      <form className="mt-5 grid gap-3 desktop:grid-cols-2" onSubmit={(event) => void addOfficer(event)}
        autoComplete="off">
        <label className="field" htmlFor="case-officer-first-name">
          <span>Case officer's first name</span>
          <input id="case-officer-first-name" className="input" value={firstName}
            maxLength={100} required disabled={busy} autoComplete="off"
            onChange={(event) => setFirstName(event.target.value)} />
        </label>
        <label className="field" htmlFor="case-officer-last-name">
          <span>Case officer's last name</span>
          <input id="case-officer-last-name" className="input" value={lastName}
            maxLength={100} required disabled={busy} autoComplete="off"
            onChange={(event) => setLastName(event.target.value)} />
        </label>
        <button type="submit" className="btn btn-primary desktop:col-span-2"
          disabled={busy || !firstName.trim() || !lastName.trim()}>
          {busy ? 'Saving…' : 'Add encrypted case officer'}
        </button>
      </form>

      {config.officers.length > 0 && <fieldset className="mt-5 border-t border-neutral-300 pt-4">
        <legend className="font-semibold">Select the assignment used for all field personnel</legend>
        <div className="mt-3 grid gap-2 desktop:grid-cols-2">
          {config.officers.map((officer) => <label key={officer.id}
            className={`flex cursor-pointer items-center gap-3 border p-3 ${officer.active
              ? 'border-accent-600 bg-accent-100' : 'border-neutral-300 bg-bg'}`}>
            <input type="radio" name="active-case-officer" value={officer.id}
              checked={officer.active} disabled={busy}
              onChange={() => void selectOfficer(officer.id)} />
            <span><strong className="block">{officer.label}</strong>
              <span className="block text-sm text-neutral-700">{MASKED_CASE_OFFICER_NAME}</span>
            </span>
          </label>)}
        </div>
      </fieldset>}
      {error && <p className="form-error mt-3" role="alert">{error}</p>}
    </section>
    {records.length === 0 ? <p className="mt-5 text-neutral-700">No wellness record has been submitted.</p>
      : <div className="mt-5 space-y-3">
        {records.map((record) => <details key={record.id} className="border border-neutral-300 bg-bg">
          <summary className="cursor-pointer list-none p-4 focus-visible:ring-2 focus-visible:ring-accent-600">
            <span className="flex flex-wrap items-start justify-between gap-3">
              <span><strong className="block">{record.reference}</strong>
                <span className="block text-sm text-neutral-700">{record.submittedBy} · {new Date(record.submittedAt).toLocaleString()}</span>
              </span>
              <span className={`tag ${record.answers ? 'tag-neutral' : 'tag-alert'}`}>
                {record.answers ? record.status : 'Unreadable'}
              </span>
            </span>
          </summary>
          <div className="border-t border-neutral-300 p-4">
            <p className="mb-3 text-xs text-neutral-700">Device: {record.deviceLabel} · Form {record.formVersion}</p>
            {record.answers ? <dl className="grid gap-x-6 desktop:grid-cols-2">
              {wellnessFields.map((field) => <div key={field.name} className="border-b border-neutral-300 py-2">
                <dt className="text-xs text-neutral-700">{field.label}</dt>
                <dd className={`mt-1 break-words font-semibold ${
                  record.answers?.[field.name] ? '' : 'text-alert-700'
                }`}>{isProtectedUacName(field.name) && record.answers?.[field.name]
                  ? MASKED_UAC_NAME : field.kind === 'officer' && record.answers?.[field.name]
                  ? MASKED_CASE_OFFICER_NAME : record.answers?.[field.name] || 'Blank by rule'}</dd>
              </div>)}
            </dl> : <p className="form-error" role="alert">This record failed cryptographic verification.</p>}
          </div>
        </details>)}
      </div>}
  </section>;
}

function SessionsTab({
  sessions, onRevoke,
}: { sessions: AdminData['sessions']; onRevoke: (id: string) => void }) {
  return (
    <ul className="grid gap-2 desktop:max-w-[900px]">
      {sessions.map((s) => (
        <li key={s.id} className="card flex items-center gap-3">
          <DevicePhoneMobileIcon className="w-6 h-6 text-accent-700 shrink-0" aria-hidden />
          <span className="flex-1 min-w-0">
            <span className="block font-heading text-base">{s.userLabel}</span>
            <span className="block text-sm text-neutral-700">
              Started {s.createdLabel} · last seen {s.lastSeenLabel}
              {s.current && ' · this session'}
            </span>
          </span>
          {!s.current && (
            <button className="btn btn-ghost shrink-0" onClick={() => onRevoke(s.id)}>
              Revoke
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * §85: audit rows carry opaque object IDs and reason codes, never names or PII.
 * The table shows exactly what is stored — nothing is enriched client-side.
 */
type AuditScope =
  | 'end-to-end' | 'browser' | 'sip-edge' | 'pbx' | 'carrier'
  | 'transcription' | 'application' | 'failures';

function browserSipDiagnostic(): Promise<DiagnosticCheck> {
  const started = Date.now();
  return new Promise((resolve) => {
    let settled = false;
    const socket = new WebSocket('wss://sip.x2f-planning.com/sip', 'sip');
    const finish = (ok: boolean, observation: string, action: string) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      socket.close();
      resolve({
        target: 'browser', label: 'Browser to secure SIP edge', ok,
        elapsedMs: Date.now() - started, observation, action,
      });
    };
    const timer = window.setTimeout(() => finish(
      false,
      'The browser could not open the secure SIP WebSocket within 5 seconds.',
      'Check this device network, browser permissions, and the SIP edge certificate.',
    ), 5_000);
    socket.onopen = () => finish(
      true,
      'The browser opened the secure SIP WebSocket.',
      'No action needed.',
    );
    socket.onerror = () => finish(
      false,
      'The browser could not open the secure SIP WebSocket.',
      'Check this device network, browser permissions, and the SIP edge certificate.',
    );
    socket.onclose = () => finish(
      false,
      'The secure SIP WebSocket closed before it became ready.',
      'Reconnect the operator phone, then run this check again.',
    );
  });
}

function auditEntryMatches(entry: AdminData['audit'][number], scope: AuditScope): boolean {
  const evidence = `${entry.event} ${entry.sourceService ?? ''} ${entry.destinationService ?? ''}`.toLowerCase();
  if (scope === 'failures') return entry.result !== 'ok';
  if (scope === 'end-to-end') return entry.event.startsWith('call_center.');
  if (scope === 'browser') return evidence.includes('browser-softphone')
    || entry.event.startsWith('call_center.client.');
  if (scope === 'sip-edge') return evidence.includes('kamailio')
    || evidence.includes('sip_edge') || evidence.includes('sip_credential');
  if (scope === 'pbx') return evidence.includes('freeswitch')
    || evidence.includes('diagnostic.pbx') || entry.event.startsWith('call_center.sip.');
  if (scope === 'carrier') return evidence.includes('telnyx') || evidence.includes('carrier')
    || Boolean(entry.details.providerCallId) || Boolean(entry.details.sipStatus);
  if (scope === 'transcription') return evidence.includes('transcript')
    || evidence.includes('translation') || evidence.includes('transcription');
  return evidence.includes('veritas-api') || entry.event.startsWith('api.')
    || evidence.includes('diagnostic.application');
}

function AuditTab({
  entries, onRunDiagnostics, onRefresh, onOpenCallCenter,
}: {
  entries: AdminData['audit'];
  onRunDiagnostics: (target: DiagnosticTarget) => Promise<DiagnosticRun>;
  onRefresh: () => Promise<void>;
  onOpenCallCenter: () => void;
}) {
  const [scope, setScope] = useState<AuditScope>('end-to-end');
  const [running, setRunning] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [diagnostic, setDiagnostic] = useState<DiagnosticRun | null>(null);
  const [diagnosticError, setDiagnosticError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();
  const visible = entries.filter((entry) => auditEntryMatches(entry, scope)
    && (!needle || `${entry.whenLabel} ${entry.event} ${entry.result} ${entry.reasonCode ?? ''} ${entry.eventId} ${entry.objectId ?? ''} ${entry.requestId ?? ''} ${entry.correlationId ?? ''} ${entry.sourceService ?? ''} ${entry.destinationService ?? ''} ${JSON.stringify(entry.details)}`
      .toLowerCase().includes(needle)));
  const scopes: Array<[AuditScope, string]> = [
    ['end-to-end', 'End to end'], ['browser', 'Browser'], ['sip-edge', 'SIP edge'],
    ['pbx', 'FreeSWITCH / PBX'], ['carrier', 'Carrier'],
    ['transcription', 'Transcription'], ['application', 'Application'],
    ['failures', 'Failures'],
  ];
  const targetForScope: DiagnosticTarget = scope === 'sip-edge' ? 'sip-edge'
    : scope === 'pbx' ? 'pbx'
      : scope === 'carrier' ? 'carrier'
        : scope === 'transcription' ? 'transcription'
          : scope === 'application' ? 'application' : 'all';

  const run = async (target: DiagnosticTarget, browserOnly = false) => {
    setRunning(true);
    setDiagnostic(null);
    setDiagnosticError(null);
    try {
      const includeBrowser = browserOnly || target === 'all';
      const [browserCheck, serverRun] = await Promise.all([
        includeBrowser ? browserSipDiagnostic() : Promise.resolve(null),
        browserOnly ? Promise.resolve(null) : onRunDiagnostics(target),
      ]);
      const checks = [browserCheck, ...(serverRun?.checks ?? [])]
        .filter((check): check is DiagnosticCheck => check !== null);
      setDiagnostic({
        traceId: serverRun?.traceId ?? crypto.randomUUID(),
        ok: checks.every((check) => check.ok),
        checks,
      });
      await onRefresh();
    } catch {
      setDiagnosticError('The diagnostic run could not finish. Existing evidence remains unchanged.');
    } finally {
      setRunning(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try { await onRefresh(); } finally { setRefreshing(false); }
  };

  return (
    <section className="card p-0 overflow-hidden" aria-labelledby="audit-heading">
      <div className="p-4 border-b border-neutral-300">
        <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="audit-heading" className="text-h4">Forensic event trail</h2>
          <p className="text-sm text-neutral-700 mt-1">
            Correlated browser, SIP, PBX, carrier, authentication, and application evidence. Secrets and protected identities are excluded.
          </p>
        </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn btn-primary" disabled={running}
              onClick={() => void run('all')}>{running ? 'Testing…' : 'Run full path check'}</button>
            <button type="button" className="btn btn-secondary" disabled={running}
              onClick={() => void run(targetForScope, scope === 'browser')}>Test this layer</button>
            <button type="button" className="btn btn-secondary" disabled={refreshing}
              onClick={() => void refresh()}>{refreshing ? 'Refreshing…' : 'Refresh evidence'}</button>
            <button type="button" className="btn btn-secondary" onClick={onOpenCallCenter}>
              Reconnect operator phone
            </button>
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto scrollbar-none mt-4" role="tablist"
          aria-label="Forensic evidence layer">
          {scopes.map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={scope === id}
              className={`seg shrink-0 ${scope === id ? 'seg-active' : ''}`}
              onClick={() => setScope(id)}>{label}</button>
          ))}
        </div>
        <label className="field mt-4 max-w-[620px]">
          <span>Find a trace, event, status, or reason</span>
          <input className="input" type="search" value={search}
            placeholder="Paste a trace ID or type 407"
            onChange={(event) => setSearch(event.target.value)} />
        </label>
      </div>

      {diagnostic && (
        <section className="p-4 border-b border-neutral-300 bg-accent-100" aria-live="polite">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-h5">Latest diagnostic: {diagnostic.ok ? 'Path ready' : 'Action needed'}</h3>
            <span className="font-mono text-xs break-all">Trace {diagnostic.traceId}</span>
          </div>
          <div className="grid gap-2 mt-3 desktop:grid-cols-2">
            {diagnostic.checks.map((check) => (
              <article key={check.target} className="card p-3">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="font-heading font-semibold">{check.label}</h4>
                  <span className={`tag ${check.ok ? 'tag-neutral' : 'tag-alert'}`}>
                    {check.ok ? 'Passed' : 'Failed'} · {check.elapsedMs} ms
                  </span>
                </div>
                <p className="text-sm mt-2">{check.observation}</p>
                <p className="text-sm text-neutral-700 mt-1"><strong>Next:</strong> {check.action}</p>
              </article>
            ))}
          </div>
        </section>
      )}
      {diagnosticError && <p className="form-error m-4" role="alert">{diagnosticError}</p>}
      <div className="overflow-x-auto">
      <p className="px-4 pt-3 text-sm text-neutral-700" aria-live="polite">
        Showing {visible.length} of the latest {entries.length} protected events.
      </p>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>
            {['When', 'Event', 'Result', 'Reason', 'Trace'].map((h) => (
              <th key={h} scope="col"
                  className="text-left p-3 font-heading text-h6 uppercase">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.map((e) => (
            <tr key={e.eventId} className="border-t border-neutral-300">
              <td className="p-3 whitespace-nowrap tabular-nums align-top">{e.whenLabel}</td>
              <td className="p-3 font-mono text-xs break-all align-top">{e.event}</td>
              <td className={`p-3 uppercase font-semibold align-top ${e.result !== 'ok' ? 'text-alert-600' : 'text-accent-700'}`}>
                {e.result}
              </td>
              <td className="p-3 text-neutral-700 font-mono text-xs align-top">
                {e.reasonCode ?? 'Not reported'}
              </td>
              <td className="p-3 align-top min-w-[260px]">
                <details>
                  <summary className="cursor-pointer font-semibold text-accent-700">View evidence</summary>
                  {(e.correlationId || e.requestId) && (
                    <button type="button" className="btn btn-ghost text-xs mt-2"
                      onClick={() => void navigator.clipboard.writeText(e.correlationId ?? e.requestId ?? '')}>
                      Copy trace ID
                    </button>
                  )}
                  <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs">
                    <dt className="text-neutral-700">Event ID</dt><dd className="font-mono break-all">{e.eventId}</dd>
                    {e.objectId && <><dt className="text-neutral-700">Object</dt><dd className="font-mono break-all">{e.objectType}: {e.objectId}</dd></>}
                    {e.correlationId && <><dt className="text-neutral-700">Correlation</dt><dd className="font-mono break-all">{e.correlationId}</dd></>}
                    {e.requestId && <><dt className="text-neutral-700">Request</dt><dd className="font-mono break-all">{e.requestId}</dd></>}
                    {(e.sourceService || e.destinationService) && <><dt className="text-neutral-700">Service path</dt><dd className="font-mono break-all">{e.sourceService ?? 'unknown'} → {e.destinationService ?? 'unknown'}</dd></>}
                    {Object.entries(e.details).map(([key, value]) => <AuditDetail key={key} name={key} value={value} />)}
                  </dl>
                </details>
              </td>
            </tr>
          ))}
          {visible.length === 0 && <tr className="border-t border-neutral-300">
            <td colSpan={5} className="p-6 text-center text-neutral-700">No events match this filter.</td>
          </tr>}
        </tbody>
      </table>
      </div>
    </section>
  );
}

function AuditDetail({ name, value }: { name: string; value: unknown }) {
  const rendered = typeof value === 'string' || typeof value === 'number'
    || typeof value === 'boolean' ? String(value) : JSON.stringify(value);
  return <><dt className="text-neutral-700">{name.replace(/([A-Z])/g, ' $1')}</dt>
    <dd className="font-mono break-all">{rendered}</dd></>;
}

/** §97: categories come from the server so the screen cannot outrun reality. */
function PolicyTab({ policy }: { policy: SecurityPolicyView }) {
  return (
    <div className="grid grid-cols-1 desktop:grid-cols-3 gap-3">
      {policy.categories.map((c) => (
        <section key={c.heading} className="card">
          <h6 className="text-accent-700 mb-2">{c.heading}</h6>
          <dl>
            {c.rows.map(([k, v]) => (
              <div key={k} className="flex gap-3 py-1">
                <dt className="text-neutral-700 flex-1">{k}</dt>
                <dd className="italic text-right">{v}</dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
}

/**
 * User Detail sheet. §5 §36.
 *
 * Role and scope are rendered as controls, but the SERVER decides whether a
 * change is permitted — `guardSelfEscalation` refuses a self-role change, a
 * self-scope widening, or granting a permission the actor does not hold, and
 * audits the attempt. Disabling the control here is a courtesy so the operator
 * is not invited to attempt something that will be refused.
 */
export function UserDetailBody({
  user, roles, scopes, isSelf, canManageRoles, canSendInvitation, invitationSending,
  canManageAccount, onRoleChange, onAvRoleChange, onScopeChange, onSendInvitation, onDisable, onUnlock,
}: {
  user: AdminUser;
  roles: RoleDefinition[];
  scopes: Array<{ id: ScopeKind; label: string }>;
  isSelf: boolean;
  canManageRoles: boolean;
  canSendInvitation: boolean;
  canManageAccount: boolean;
  invitationSending: boolean;
  onRoleChange: (role: string) => void;
  onAvRoleChange: (enabled: boolean) => void;
  onScopeChange: (scope: ScopeKind) => void;
  onSendInvitation: () => void;
  onDisable: () => void;
  onUnlock: () => void;
}) {
  const current = user.roles.find((role) => role.role !== 'AV') ?? user.roles[0];
  const hasAvRole = user.roles.some((role) => role.role === 'AV');
  const locked = isSelf || !canManageRoles;

  return (
    <div>
      <p className="text-base mb-1">{user.displayName}</p>
      <p className="text-base text-neutral-700 mb-4">
        {user.username ?? 'Workspace SSO only'} · {effectiveStatus(user)}
      </p>

      {isSelf && (
        <p className="mb-4 p-3 bg-neutral-200 border border-neutral-400 rounded-lg text-base">
          You cannot change your own role or scope.
        </p>
      )}

      <label className="field">
        <span>Role</span>
        <select
          className="input"
          value={current?.role ?? ''}
          disabled={locked}
          onChange={(e) => onRoleChange(e.target.value)}
        >
          {roles.map((r) => (
            // §36: Super Administrator invariants are not editable through the
            // ordinary permission surface.
            <option key={r.name} value={r.name} disabled={r.locked || r.name === 'AV'}>
              {r.name}{r.locked ? ' (protected system role)' : ''}
            </option>
          ))}
        </select>
      </label>

      <label className="field mt-4 flex items-center gap-3">
        <input type="checkbox" checked={hasAvRole} disabled={locked}
          onChange={(event) => onAvRoleChange(event.target.checked)} />
        <span>AV reviewer access</span>
      </label>
      <p className="text-sm text-neutral-700 mt-1">
        AV access is additional. It does not replace this person’s existing role.
      </p>

      <label className="field">
        <span>Data scope</span>
        <select
          className="input"
          value={current?.scope ?? 'own'}
          disabled={locked}
          onChange={(e) => onScopeChange(e.target.value as ScopeKind)}
        >
          {scopes.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
      </label>

      <p className="text-base text-neutral-700 mb-4">
        A user needs both a permission and a matching data scope.
      </p>

      <div className="flex flex-col gap-2 desktop:flex-row">
        {user.status === 'invited' && canSendInvitation && (
          <button
            className="btn btn-primary"
            onClick={onSendInvitation}
            disabled={invitationSending}
          >
            {invitationSending ? 'Sending…' : 'Resend invitation'}
          </button>
        )}
        {(user.status === 'disabled' || user.status === 'locked' || user.loginLocked) ? (
          <button
            className="btn btn-primary"
            onClick={onUnlock}
            disabled={isSelf || !canManageAccount}
          >
            Unlock account
          </button>
        ) : (
          <button
            className="btn btn-secondary"
            onClick={onDisable}
            disabled={isSelf || !canManageAccount}
          >
            Disable account
          </button>
        )}
      </div>
    </div>
  );
}
